import { generateAbsenceHoursBody } from '../../../helpers/api/absencesApi'
import { LeaveType, PayrollPeriodViewModel } from '../../../models'
import { authorizedFetch } from '../../helpers/authorizedFetch'
import trans from '../../helpers/trans'
import { generateApiUrl } from '../../helpers/url'
import getIncluded from '../../japi/getIncluded'
import isResourceDocument from '../../japi/guards/isResourceDocument'
import { JapiDocument } from '../../japi/types/Document'
import { PzLeaveType } from '../../types/leaveType'
import { FetchCollectionResult, FetchResult, FetchResultType } from '../FetchResult'
import { LeaveOfAbsenceResource } from '../LeaveOfAbsence/LeaveOfAbsence'
import { transformToLeaveOfAbsence } from '../LeaveOfAbsence/LeaveOfAbsenceTransformers'
import { NotificationCenterHeaderName, NotificationCenterLog } from '../NotificationCenter/NotificationCenter'
import { transformLegacyPayrollPeriodToPayrollPeriod } from '../PayrollPeriod/PayrollPeriodTransformers'
import { UserResource } from '../User/User'
import { transformToBasicUser } from '../User/UserTransformers'
import { LeaveOfAbsenceHour, LeaveOfAbsenceHourResource, LoketLeaveOfAbsenceHour } from './LeaveOfAbsenceHour'
import { transformToLeaveOfAbsenceHour, transformToLoketLeaveOfAbsenceHour } from './LeaveOfAbsenceHourTransformers'

export const getLoketLeaveOfAbsencesHoursApiCall = async (
    leaveOfAbsencesHourId: string,
): Promise<FetchCollectionResult<LoketLeaveOfAbsenceHour, string>> => {
    try {
        const includes = [
            'leaveOfAbsence',
            'leaveOfAbsence.user',
        ]

        const url = generateApiUrl({
            endpoint: `/leave-of-absence-hours/${leaveOfAbsencesHourId}`,
            queryParams: {
                include: includes.join(','),
            },
        })

        const response = await authorizedFetch(url, { method: 'GET' })
        const doc = await response.json()

        if (! isResourceDocument<LeaveOfAbsenceHourResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const leaveOfAbsenceResource = getIncluded(doc, doc.data, 'leaveOfAbsence') as LeaveOfAbsenceResource
        const leaveOfAbsenceUserResource = getIncluded(doc, leaveOfAbsenceResource, 'user') as UserResource

        const user = transformToBasicUser(leaveOfAbsenceUserResource)
        const leaveOfAbsence = transformToLeaveOfAbsence(leaveOfAbsenceResource, user)
        const data = transformToLoketLeaveOfAbsenceHour(doc.data, leaveOfAbsence)

        return {
            amountOfPages: 1,
            status: 200,
            type: FetchResultType.Success,
            data,
        }
    } catch (error) {
        console.error('[getLoketLeaveOfAbsencesHoursApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const postLeaveOfAbsenceHoursApiCall = async (
    absenceId: string,
    eventLogTrackerData: NotificationCenterLog,
    payrollPeriodId?: string,
    hours?: number,
    weekNumber?: number,
    leaveType?: LeaveType | PzLeaveType,
): Promise<FetchResult<LeaveOfAbsenceHour, string>> => {
    try {
        const includes = ['payrollPeriod']

        const url = generateApiUrl({
            endpoint: '/leave-of-absence-hours',
            queryParams: {
                include: includes.join(','),
            },
        })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify({
                data: generateAbsenceHoursBody(
                    absenceId,
                    payrollPeriodId,
                    hours,
                    weekNumber,
                    'leaveOfAbsenceHours',
                    leaveType,
                ),
            }),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<LeaveOfAbsenceHourResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const includedPayrollPeriod = getIncluded(doc, doc.data, 'payrollPeriod')

        const payrollPeriodResource = {
            id: includedPayrollPeriod?.id,
            type: includedPayrollPeriod?.type,
            ...includedPayrollPeriod?.attributes,
        } as PayrollPeriodViewModel

        const payrollPeriod = transformLegacyPayrollPeriodToPayrollPeriod(payrollPeriodResource)
        const leaveOfAbsenceHour = transformToLeaveOfAbsenceHour(doc.data, payrollPeriod)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: leaveOfAbsenceHour,
        }
    } catch (error) {
        console.error('[postLeaveOfAbsenceHoursApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const patchLeaveOfAbsenceHoursApiCall = async (
    leaveOfAbsenceHourId: string,
    absenceId: string,
    eventLogTrackerData: NotificationCenterLog,
    payrollPeriodId?: string,
    hours?: number | null,
    weekNumber?: number,
    leaveType?: LeaveType | PzLeaveType,
): Promise<FetchResult<LeaveOfAbsenceHour, string>> => {
    try {
        const includes = ['payrollPeriod']

        const url = generateApiUrl({
            endpoint: `/leave-of-absence-hours/${leaveOfAbsenceHourId}`,
            queryParams: {
                include: includes.join(','),
            },
        })

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            body: JSON.stringify({
                data: {
                    id: leaveOfAbsenceHourId,
                    ...generateAbsenceHoursBody(
                        absenceId,
                        payrollPeriodId,
                        hours,
                        weekNumber,
                        'leaveOfAbsenceHours',
                        leaveType,
                    ),
                },
            }),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<LeaveOfAbsenceHourResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const includedPayrollPeriod = getIncluded(doc, doc.data, 'payrollPeriod')

        const payrollPeriodResource = {
            id: includedPayrollPeriod?.id,
            type: includedPayrollPeriod?.type,
            ...includedPayrollPeriod?.attributes,
        } as PayrollPeriodViewModel

        const payrollPeriod = transformLegacyPayrollPeriodToPayrollPeriod(payrollPeriodResource)
        const leaveOfAbsenceHour = transformToLeaveOfAbsenceHour(doc.data, payrollPeriod)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: leaveOfAbsenceHour,
        }
    } catch (error) {
        console.error('[patchLeaveOfAbsenceHoursApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const deleteLeaveOfAbsenceHoursApiCall = async (
    leaveOfAbsenceHourId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<any, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/leave-of-absence-hours${leaveOfAbsenceHourId}`,
        })

        const response = await authorizedFetch(url, {
            method: 'DELETE',
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: {},
        }
    } catch (error) {
        console.error('[deleteLeaveOfAbsenceHoursApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}
